import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, authHelpers } from '../services/authApi';
import { googleAuth } from '../config/supabase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is authenticated on app load (session may have been set
  // by a login on this domain, or carried over via the shared .tecnvi-ai.com
  // cookie from the dashboard app).
  useEffect(() => {
    const checkAuth = async () => {
      const isLoggedIn = authHelpers.isAuthenticated();

      if (isLoggedIn) {
        const savedUser = authHelpers.getCurrentUser();
        if (savedUser) {
          setUser(savedUser);
          setIsAuthenticated(true);
        }

        try {
          const response = await authAPI.getProfile();
          const userData = response.data.user;
          setUser(userData);
          setIsAuthenticated(true);
          authHelpers.setCurrentUser(userData);
        } catch (error) {
          if (error.response?.status === 401) {
            authHelpers.clearAuth();
            setUser(null);
            setIsAuthenticated(false);
          }
          // Other errors (network, etc.) — keep whatever we optimistically loaded
        }
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  const signup = async (userData) => {
    try {
      const response = await authAPI.signup(userData);
      return {
        success: true,
        user: response.data.user,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Signup failed'
      };
    }
  };

  const login = async (loginIdentifier, password, authType = 'email') => {
    try {
      const response = await authAPI.login({
        email: loginIdentifier,
        identifier: loginIdentifier,
        authType,
        ...(authType === 'phone' ? { phone: loginIdentifier } : {}),
        password
      });
      const { user: userData, tokens, tokenBonus } = response.data;

      authHelpers.setAuthData(tokens, userData);
      setUser(userData);
      setIsAuthenticated(true);

      return { success: true, user: userData, tokenBonus };
    } catch (error) {
      const errorData = error.response?.data;
      return {
        success: false,
        error: errorData?.error || 'Login failed',
        code: errorData?.code,
        userId: errorData?.userId
      };
    }
  };

  const adminSignup = async (signupData) => {
    try {
      const response = await authAPI.adminSignup(signupData);
      if (response.data.success) {
        return { success: true, message: response.data.message };
      }
      return { success: false, error: response.data.error || 'Admin signup failed.' };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'An unexpected error occurred.' };
    }
  };

  const adminLogin = async (loginIdentifier, password, authType = 'email') => {
    try {
      const response = await authAPI.adminLogin({
        email: loginIdentifier,
        identifier: loginIdentifier,
        authType,
        ...(authType === 'phone' ? { phone: loginIdentifier } : {}),
        password
      });

      if (response.data.success) {
        const { user: adminData, tokens } = response.data;
        authHelpers.setAuthData(tokens, adminData);
        setUser(adminData);
        setIsAuthenticated(true);
        return { success: true, user: adminData };
      }
      return { success: false, error: response.data.error || 'Login failed' };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Invalid credentials.' };
    }
  };

  const handleAdminLoginSuccess = () => {
    // Redirect to the dashboard app is handled by the modal after login succeeds.
  };

  const verifyEmail = async (identifier, otp, authType = 'email') => {
    try {
      const payload = {
        email: identifier,
        identifier,
        authType,
        ...(authType === 'phone' ? { phone: identifier } : {}),
        otp
      };

      const response = await authAPI.verifyEmail(payload);
      const { user: userData, tokenBonus } = response.data;

      if (response.data.tokens) {
        authHelpers.setAuthData(response.data.tokens, userData);
        setUser(userData);
        setIsAuthenticated(true);
      }

      return {
        success: true,
        user: userData,
        message: response.data.message,
        tokenBonus
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Email verification failed'
      };
    }
  };

  const resendVerification = async (identifierOrPayload) => {
    try {
      const response = await authAPI.resendVerification(identifierOrPayload);
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resend verification code'
      };
    }
  };

  const forgotPassword = async (payload, authType = 'email') => {
    try {
      const postData = typeof payload === 'string'
        ? { email: payload, authType: 'email' }
        : { phone: payload.phone, method: payload.method, authType: 'phone' };

      const response = await authAPI.forgotPassword(postData);
      return {
        success: true,
        message: response.data.message,
        requiresMethodSelection: response.data.requiresMethodSelection,
        methods: response.data.methods
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send password reset code',
        requiresMethodSelection: error.response?.data?.requiresMethodSelection,
        methods: error.response?.data?.methods
      };
    }
  };

  const resetPassword = async (payload, otp, newPassword, authType = 'email') => {
    try {
      const postData = authType === 'phone'
        ? { phone: payload, otp, newPassword, authType }
        : { email: payload, otp, newPassword, authType };

      const response = await authAPI.resetPassword(postData);
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Password reset failed'
      };
    }
  };

  const adminForgotPassword = async (payload, authType = 'email') => {
    try {
      const postData = typeof payload === 'string'
        ? { email: payload, authType: 'email' }
        : { phone: payload.phone, method: payload.method, authType: 'phone' };

      const response = await authAPI.adminForgotPassword(postData);
      return {
        success: true,
        message: response.data.message,
        requiresMethodSelection: response.data.requiresMethodSelection,
        methods: response.data.methods
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send password reset code',
        requiresMethodSelection: error.response?.data?.requiresMethodSelection,
        methods: error.response?.data?.methods
      };
    }
  };

  const adminResetPassword = async (payload, otp, newPassword, authType = 'email') => {
    try {
      const postData = authType === 'phone'
        ? { phone: payload, otp, newPassword, authType }
        : { email: payload, otp, newPassword, authType };

      const response = await authAPI.adminResetPassword(postData);
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Password reset failed'
      };
    }
  };

  const rbacForgotPassword = async (payload) => {
    try {
      const response = await authAPI.rbacForgotPassword(payload);
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send reset code'
      };
    }
  };

  const rbacResetPassword = async (data) => {
    try {
      const response = await authAPI.rbacResetPassword(data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  };

  const adminRbacForgotPassword = async (data) => {
    try {
      const response = await authAPI.adminRbacForgotPassword(data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  };

  const adminRbacResetPassword = async (data) => {
    try {
      const response = await authAPI.adminRbacResetPassword(data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  };

  // Google OAuth methods
  const signInWithGoogle = async () => {
    try {
      const result = await googleAuth.signInWithGoogle();
      if (result.success) {
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (error) {
      return { success: false, error: 'Google sign in failed' };
    }
  };

  const signUpWithGoogle = async () => {
    try {
      const result = await googleAuth.signUpWithGoogle();
      if (result.success) {
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (error) {
      return { success: false, error: 'Google sign up failed' };
    }
  };

  const handleGoogleAuthCallback = async (session) => {
    try {
      const response = await authAPI.googleAuth({
        supabaseUser: session.user,
        accessToken: session.access_token,
        refreshToken: session.refresh_token
      });

      const { user: userData, tokens } = response.data;

      if (!tokens?.accessToken) {
        throw new Error('Authentication tokens not received');
      }

      authHelpers.setAuthData(tokens, userData);
      setUser(userData);
      setIsAuthenticated(true);

      // Clear Supabase's own session so it doesn't conflict with our custom JWT tokens
      try {
        const { supabase } = await import('../config/supabase');
        await supabase.auth.signOut({ scope: 'local' });
      } catch (supabaseError) {
        // non-critical
      }

      return { success: true, user: userData, tokens };
    } catch (error) {
      if (error.response?.status === 409) {
        return { success: false, error: 'Multiple login attempts detected. Please try again in a moment.' };
      }
      if (error.response?.status === 429) {
        const retryAfter = error.response?.data?.retryAfter;
        return {
          success: false,
          error: retryAfter ? `Please wait ${retryAfter} seconds before trying again.` : 'Too many requests. Please wait and try again.'
        };
      }
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to complete Google authentication'
      };
    }
  };

  const logout = async () => {
    setUser(null);
    setIsAuthenticated(false);
    authHelpers.clearAuth();
    localStorage.removeItem('hms_client_id');
    window.location.href = '/';
  };

  const value = {
    // State
    user,
    loading,
    isAuthenticated,

    // Auth methods
    signup,
    login,
    adminSignup,
    adminLogin,
    handleAdminLoginSuccess,
    logout,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
    adminForgotPassword,
    adminResetPassword,
    rbacForgotPassword,
    rbacResetPassword,
    adminRbacForgotPassword,
    adminRbacResetPassword,

    // Google OAuth methods
    signInWithGoogle,
    signUpWithGoogle,
    handleGoogleAuthCallback,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
